'use client';

import { useEffect } from 'react';
import { useProjects, useProject } from '@/features/project/hooks/use-projects';
import { useUrlProjectId } from '@/features/project/hooks/use-active-project';
import { useLastProject } from '@/shared/hooks/use-last-project';
import { ApiClientError } from '@/shared/lib/api';

/**
 * The single owner of the persisted "active building" pointer
 * (`lastProjectId`). Mounted ONCE in the authed layout, so it tracks the URL on
 * every route regardless of which nav is visible.
 *
 * Why a dedicated always-mounted component instead of doing this inside a nav:
 * the desktop sidebar (`AppSidebar`) is only rendered on wide + expanded screens
 * (DesktopShell drops it when `collapsed` or `bare`). It used to be the only
 * writer, so on narrow screens or with the sidebar collapsed the active building
 * was NEVER persisted — opening a concept didn't stick, and the floating bottom
 * bar (`MobileNavigation`, which read `projects[0]`) showed the newest building
 * instead. Centralising the write here means it runs everywhere, and both navs
 * read the same value via `useActiveProjectId`.
 *
 * Renders nothing.
 */
export function ActiveProjectSync() {
  const urlProjectId = useUrlProjectId();
  const { lastProjectId, updateLastProject, clearLastProject } =
    useLastProject();
  const { data: projectsResponse } = useProjects();
  const firstProject = projectsResponse?.data?.[0];

  const effectiveProjectId =
    urlProjectId || lastProjectId || firstProject?.id || null;
  const { error } = useProject(effectiveProjectId || '', {
    enabled: !!effectiveProjectId,
  });
  const projectIsDead =
    error instanceof ApiClientError && error.isNotFoundError();

  // EAGER persist: the active building follows the URL the instant you open a
  // concept/building (incl. deep links from Recent and the home-page switcher,
  // which both navigate to `/project/{id}`). Not gated on the building's fetch
  // resolving — the choice must stick immediately so a later jump to
  // Recent/Account/Help reads it back instead of falling through to firstProject.
  useEffect(() => {
    if (urlProjectId && urlProjectId !== lastProjectId) {
      updateLastProject(urlProjectId);
    }
  }, [urlProjectId, lastProjectId, updateLastProject]);

  // Clear a stale lastProjectId when its own fetch 404s (deleted building or a
  // since-switched/since-reset account). List-absence is NOT a death signal; a
  // 404 is terminal/not retried (query-client retry policy). The
  // `urlProjectId !== lastProjectId` guard prevents a persist↔clear ping-pong:
  // while you sit ON a dead building's URL the eager-persist above keeps that id
  // stored, so clearing it every render would spin an infinite 404→429 storm. We
  // skip the clear there (the page shows its "Back to buildings" 404 card) and
  // heal the stale id the moment you leave for a secondary page.
  useEffect(() => {
    if (
      lastProjectId &&
      effectiveProjectId === lastProjectId &&
      urlProjectId !== lastProjectId &&
      projectIsDead
    ) {
      clearLastProject();
    }
  }, [
    lastProjectId,
    effectiveProjectId,
    urlProjectId,
    projectIsDead,
    clearLastProject,
  ]);

  // Seed firstProject as the active building only when nothing is stored yet and
  // we're not on a project URL — a sensible default on a fresh session that
  // never overwrites an existing choice.
  useEffect(() => {
    if (!urlProjectId && !lastProjectId && firstProject?.id) {
      updateLastProject(firstProject.id);
    }
  }, [urlProjectId, lastProjectId, firstProject?.id, updateLastProject]);

  return null;
}
